import { useCatalogFilterStore } from '@/stores/catalog-filter-store';
import {
  Box,
  Drawer,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  FilterList,
  GridView,
  Search,
  SwapVert,
  ViewList,
} from '@mui/icons-material';
import { theme } from '@/config/theme';
import { useCallback, useState } from 'react';
import { ItemsGrid } from './components/items-grid';
import { CatalogBrowse } from './components/catalog-browse';
import { FacetSidebar } from './components/facet-sidebar';
import { SortPanel } from './components/sort-panel';
import { useItemsDataHook } from './hooks/use-items-data.hook';
import { useFilteredItems } from './hooks/use-filtered-items.hook';

type ActivePanel = 'filter' | 'sort' | null;

export function ExternalItemsPage() {
  const { viewMode, setViewMode, searchText, setSearchText } =
    useCatalogFilterStore();
  const { itemsData, isLoading } = useItemsDataHook();
  const filteredItems = useFilteredItems(itemsData);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [scrollParent, setScrollParent] = useState<HTMLDivElement | null>(null);
  const scrollRef = useCallback((node: HTMLDivElement | null) => {
    setScrollParent(node);
  }, []);
  const [activePanel, setActivePanel] = useState<ActivePanel>(
    isMobile ? null : 'filter',
  );

  const togglePanel = (panel: 'filter' | 'sort') => {
    setActivePanel((prev) => (prev === panel ? null : panel));
  };

  return (
    <Box>
      {/* Shared header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: { xs: 1.5, sm: 2 },
          py: 1,
          backgroundColor: 'background.default',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {/* Panel toggles */}
        <Box
          sx={{
            display: 'flex',
            gap: 0.25,
            flexShrink: 0,
            backgroundColor: 'white',
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            p: 0.25,
          }}
        >
          <Tooltip title="Filter">
            <IconButton
              size="small"
              onClick={() => togglePanel('filter')}
              sx={{
                borderRadius: 0.5,
                backgroundColor:
                  activePanel === 'filter' ? 'primary.main' : 'transparent',
                color:
                  activePanel === 'filter' ? 'white' : 'text.secondary',
                '&:hover': {
                  backgroundColor:
                    activePanel === 'filter'
                      ? 'primary.dark'
                      : 'action.hover',
                },
              }}
            >
              <FilterList sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Sortieren">
            <IconButton
              size="small"
              onClick={() => togglePanel('sort')}
              sx={{
                borderRadius: 0.5,
                backgroundColor:
                  activePanel === 'sort' ? 'primary.main' : 'transparent',
                color:
                  activePanel === 'sort' ? 'white' : 'text.secondary',
                '&:hover': {
                  backgroundColor:
                    activePanel === 'sort'
                      ? 'primary.dark'
                      : 'action.hover',
                },
              }}
            >
              <SwapVert sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <TextField
          size="small"
          placeholder="Artikel suchen..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          fullWidth
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 20, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            backgroundColor: 'white',
            '& .MuiOutlinedInput-root': { fontSize: '0.85rem' },
          }}
        />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ flexShrink: 0, fontSize: '0.8rem' }}
        >
          {filteredItems.length} Artikel
        </Typography>

        {/* View toggle */}
        <Box
          sx={{
            display: 'flex',
            gap: 0.25,
            flexShrink: 0,
            backgroundColor: 'white',
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            p: 0.25,
          }}
        >
          <Tooltip title="Katalog-Ansicht">
            <IconButton
              size="small"
              onClick={() => setViewMode('cards')}
              sx={{
                borderRadius: 0.5,
                backgroundColor:
                  viewMode === 'cards' ? 'primary.main' : 'transparent',
                color: viewMode === 'cards' ? 'white' : 'text.secondary',
                '&:hover': {
                  backgroundColor:
                    viewMode === 'cards' ? 'primary.dark' : 'action.hover',
                },
              }}
            >
              <GridView sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Tabellen-Ansicht">
            <IconButton
              size="small"
              onClick={() => setViewMode('grid')}
              sx={{
                borderRadius: 0.5,
                backgroundColor:
                  viewMode === 'grid' ? 'primary.main' : 'transparent',
                color: viewMode === 'grid' ? 'white' : 'text.secondary',
                '&:hover': {
                  backgroundColor:
                    viewMode === 'grid' ? 'primary.dark' : 'action.hover',
                },
              }}
            >
              <ViewList sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          height: {
            xs: 'calc(100dvh - 126px)',
            sm: 'calc(100dvh - 154px)',
          },
          overflow: 'hidden',
        }}
      >
        {/* Desktop side panel */}
        {!isMobile && activePanel != null && (
          <Box
            sx={{
              width: 240,
              minWidth: 240,
              maxWidth: 240,
              flexShrink: 0,
              borderRight: 1,
              borderColor: 'divider',
              backgroundColor: 'white',
              overflowY: 'auto',
              overflowX: 'hidden',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(2, 52, 76, 0.12) transparent',
              '&::-webkit-scrollbar': { width: 5 },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(2, 52, 76, 0.12)',
                borderRadius: 10,
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: 'rgba(2, 52, 76, 0.25)',
              },
            }}
          >
            {activePanel === 'filter' ? <FacetSidebar /> : <SortPanel />}
          </Box>
        )}

        {/* Mobile drawer */}
        {isMobile && (
          <Drawer
            open={activePanel != null}
            onClose={() => setActivePanel(null)}
            slotProps={{
              paper: {
                sx: {
                  width: 260,
                  overflowX: 'hidden',
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgba(2, 52, 76, 0.12) transparent',
                  '&::-webkit-scrollbar': { width: 5 },
                  '&::-webkit-scrollbar-track': { background: 'transparent' },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(2, 52, 76, 0.12)',
                    borderRadius: 10,
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: 'rgba(2, 52, 76, 0.25)',
                  },
                },
              },
            }}
          >
            {activePanel === 'filter' ? <FacetSidebar /> : <SortPanel />}
          </Drawer>
        )}

        {/* Main content */}
        <Box
          ref={scrollRef}
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            px: { xs: 1.5, sm: 2 },
            py: 1.5,
            backgroundColor: 'background.default',
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(2, 52, 76, 0.12) transparent',
            '&::-webkit-scrollbar': { width: 5 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(2, 52, 76, 0.12)',
              borderRadius: 10,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: 'rgba(2, 52, 76, 0.25)',
            },
          }}
        >
          {viewMode === 'cards' ? (
            <CatalogBrowse items={filteredItems} isLoading={isLoading} scrollParent={scrollParent} />
          ) : (
            <ItemsGrid items={filteredItems} isLoading={isLoading} />
          )}
        </Box>
      </Box>
    </Box>
  );
}
